export function formatAddressWithBuildingAndUnitNumber(address) {
  const buildingUnitString = `${
    address?.building?.name ? `${address.building.name} - ` : ""
  }${address?.unitNo ? ` ${address.unitNo}/` : " "}`;
  return `${buildingUnitString}${address?.streetNo || ""} ${
    address?.address || ""
  } ${address?.suburb || ""} ${address?.state || ""} ${
    address?.postCode || ""
  }`;
}

export function formatAccountName(account) {
  return `${account?.name || ""} ${account?.accountProvider?.name || ""} ${
    account?.user?.userName || ""
  }`;
}

export const ProvisioningState = {
  PLANNED: "planned",
  LOCAL_NETWORK_READY: "local_network_ready",
  FIBRE_INSTALLED: "fibre_installed",
  NTD_INSTALLED: "ntd_installed",
  READY_FOR_SERVICE: "ready_for_service",
  SERVICE_ACTIVE: "service_active",
};

export const ProvisioningStateSelection = Object.entries(ProvisioningState).map(
  ([key, value]) => ({name: formatAddressProvisioningStatus(key), value}),
);

export const MapItemType = {
  AREA: "area",
  NODE: "node",
  IMAGE: "image",
};

export function formatMapItemType(type) {
  return type ? type.charAt(0) + type.slice(1).toLowerCase() : "";
}

export const MapItemTypeSelection = Object.entries(MapItemType).map(
  ([key, value]) => ({ name: formatMapItemType(key), value })
);

export function formatAddressProvisioningStatus(status) {
  return status ? status.replace(/_/g, " ").toUpperCase() : "";
}

export const LocationType = {
  AREA: "area",
  NODE: "node",
  IMAGE: "image",
};

export function formatLocationType(type) {
  return type ? type.charAt(0) + type.slice(1).toLowerCase() : "";
}

export const LocationTypeSelection = Object.entries(LocationType).map(
  ([key, value]) => ({name: formatLocationType(key), value}),
);

export const StatusType = {
  Information: "Information",
  MaintenanceScheduled: "MaintenanceScheduled",
  MaintenanceEmergency: "MaintenanceEmergency",
  Outage: "Outage",
  Degraded: "Degraded",
};

// Explicit UI labels
const StatusTypeLabel = {
  Information: "Information",
  MaintenanceScheduled: "Maintenance Scheduled",
  MaintenanceEmergency: "Maintenance Emergency",
  Outage: "Outage",
  Degraded: "Degraded",
};

export function formatStatusType(value) {
  if (!value) {
    return "";
  }
  if (StatusTypeLabel[value]) {
    return StatusTypeLabel[value];
  }

  let formatted = value
    .replace(/\s*\(\s*/g, " (")
    .replace(/\s*\)\s*/g, ")")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .trim();

  formatted = formatted.replace(/^([^(]+)/, (m) =>
    m
      .trim()
      .split(/\s+/)
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
      .join(" "),
  );
  formatted = formatted.replace(/\(([^)]+)\)/, (m, p1) => {
    const t = p1.trim();
    return `(${t.charAt(0).toUpperCase() + t.slice(1).toLowerCase()})`;
  });

  return formatted;
}
export const StatusTypeSelection = Object.entries(StatusType).map(
  ([key, value]) => ({name: formatStatusType(value), value}),
);

export const StatusState = {
  Pending: "Pending",
  Active: "Active",
  Investigating: "Investigating",
  Identified: "Identified",
  Monitoring: "Monitoring",
  Extended: "Extended",
  Resolved: "Resolved",
};
export const MaintenanceState = {
  Pending: "Pending",
  Active: "Active",
  Monitoring: "Monitoring",
  Extended: "Extended",
  Resolved: "Resolved",
};

export const StatusStateSelection = Object.entries(StatusState).map(
  ([key, value]) => ({name: formatLocationType(key), value}),
);

export const MaintenanceStateSelection = Object.entries(MaintenanceState).map(
  ([key, value]) => ({name: formatLocationType(key), value}),
);

export const PriorityEnum = {
  Major: "Major",
  SignificantLocalOutage: "SignificantLocalOutage",
  P1: "P1",
  P2: "P2",
  P3: "P3",
  None: "None",
};

export const PriorityLabel = {
  Major: "Major",
  SignificantLocalOutage: "Significant Local Outage",
  P1: "P1",
  P2: "P2",
  P3: "P3",
  None: "None",
};

export function formatPriority(value) {
  if (!value) {
    return "";
  }
  if (PriorityLabel[value]) {
    return PriorityLabel[value];
  }

  let formatted = value
    .replace(/\s*\(\s*/g, " (")
    .replace(/\s*\)\s*/g, ")")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .trim();

  formatted = formatted.replace(/^([^(]+)/, (m) =>
    m
      .trim()
      .split(/\s+/)
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
      .join(" "),
  );
  formatted = formatted.replace(/\(([^)]+)\)/, (m, p1) => {
    const t = p1.trim();
    return `(${t.charAt(0).toUpperCase() + t.slice(1).toLowerCase()})`;
  });

  return formatted;
}

export function formatPriorityEnum(key) {
  if (!key) {
    return "";
  }
  const withSpaces = key
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2");
  return withSpaces;
}
export const PrioritySelection = Object.entries(PriorityEnum).map(
  ([key, value]) => ({name: formatPriorityEnum(key), value}),
);

export const CauseType = {...StatusType};

export const CauseTypeSelection = Object.entries(CauseType).map(
  ([key, value]) => ({name: formatStatusType(value), value}),
);

export function leftPad(value, length, pad) {
  while (value.length < length) {
    value = pad + value;
  }
  return value;
}

export function sanitizeData(data) {
  // TODO: can add other sanitization

  if (Array.isArray(data)) {
    return data.map((d) => sanitizeData(d));
  } else if (typeof data === "object" && data !== null) {
    let newData = {...data};

    Object.keys(newData).forEach((key) => {
      const currentField = newData[key];

      if (typeof currentField === "string") {
        newData[key] = currentField.trim();
      } else if (Array.isArray(currentField)) {
        newData[key] = currentField.map((cf) => sanitizeData(cf));
      } else if (typeof currentField === "object" && currentField !== null) {
        newData[key] = sanitizeData(currentField);
      }
    });

    return newData;
  }

  return data;
}
